import React, { useState, useEffect } from 'react';
import { ModernPutAwayList } from './ModernPutAwayList';
import { ModernPutAwayDetails } from './ModernPutAwayDetails';
import { putAwayService } from '../../../../services/putAwayService';
import { logger } from '../../../../services/loggingService';
import { StcIntRelOrderDto } from '../../../../types/putaway';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { LockConflictError } from '../../../../utils/lockConflictError';

interface ModernPutAwayAppProps {
  initialStcOrderNo?: number;
  onBack?: () => void;
}

export const ModernPutAwayApp: React.FC<ModernPutAwayAppProps> = ({ initialStcOrderNo, onBack }) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<'list' | 'putaway'>('list');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [putAwayOrders, setPutAwayOrders] = useState<StcIntRelOrderDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    logger.info('ModernPutAwayApp', 'Component mounted, loading put-away orders', {
      initialStcOrderNo
    });
    loadPutAwayOrders();
  }, []);

  // Check if we should auto-navigate to a specific order
  useEffect(() => {
    if (initialStcOrderNo && initialStcOrderNo > 0 && putAwayOrders.length > 0 && !loading) {
      const order = putAwayOrders.find(o => o.stcOrderNo === initialStcOrderNo);
      if (order) {
        logger.info('ModernPutAwayApp', 'Auto-navigating to put-away order from receipt', {
          stcOrderNo: initialStcOrderNo
        });
        handleSelectPutAway(order);
      } else {
        logger.warn('ModernPutAwayApp', 'Initial StcOrderNo not found in available orders', {
          stcOrderNo: initialStcOrderNo,
          availableOrders: putAwayOrders.map(o => o.stcOrderNo)
        });
      }
    }
  }, [initialStcOrderNo, putAwayOrders, loading]);

  const loadPutAwayOrders = async () => {
    const startTime = Date.now();
    
    try {
      setLoading(true);
      setError(null);
      
      logger.info('ModernPutAwayApp', 'Starting to load put-away orders');
      
      // Bruker getAvailablePutAwayOrdersForOperator som håndterer lås-logikken og statusfiltrering
      const orders = await putAwayService.getAvailablePutAwayOrdersForOperator();
      
      const duration = Date.now() - startTime;
      logger.info('ModernPutAwayApp', 'Successfully loaded put-away orders', {
        count: orders.length,
        duration,
        availableOrders: orders.filter(o => !o.lockedBy || o.lockedBy.trim() === '').length,
        lockedOrders: orders.filter(o => o.lockedBy && o.lockedBy.trim() !== '').length
      });
      
      setPutAwayOrders(orders);
    } catch (err) {
      const duration = Date.now() - startTime;
      const error = err as Error;
      
      // Strukturert error logging med kontekst
      logger.logException('ModernPutAwayApp', error, {
        operation: 'loadPutAwayOrders',
        duration,
        userAction: 'loadData'
      });
      
      setError(t('warehouse.modules.putaway.errors.loadOrdersFailed'));
      
      // Log user-visible error
      logger.warn('ModernPutAwayApp', 'User shown error message for failed put-away orders load', {
        errorMessage: t('warehouse.modules.putaway.errors.loadOrdersFailed'),
        originalError: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPutAway = async (putAwayOrder: StcIntRelOrderDto) => {
    const startTime = Date.now();
    
    // Log user action
    logger.logUserAction('select_putaway_order', {
      stcOrderNo: putAwayOrder.stcOrderNo,
      priority: putAwayOrder.orderPriority,
      status: putAwayOrder.orderStatus,
      isLocked: !!putAwayOrder.lockedBy,
      lockedBy: putAwayOrder.lockedBy
    });
    
    try {
      // Låse utplasseringsordren til brukeren hvis det ikke allerede er låst
      if (!putAwayOrder.lockedBy || putAwayOrder.lockedBy.trim() === '') {
        logger.info('ModernPutAwayApp', 'Attempting to lock put-away order before starting', {
          stcOrderNo: putAwayOrder.stcOrderNo,
          description1: putAwayOrder.description1,
          priority: putAwayOrder.orderPriority
        });
        
        await putAwayService.lockPutAwayOrder(putAwayOrder.stcOrderNo);
        
        logger.info('ModernPutAwayApp', 'Successfully locked put-away order', {
          stcOrderNo: putAwayOrder.stcOrderNo
        });
      } else {
        logger.info('ModernPutAwayApp', 'Put-away order already locked, proceeding to start', {
          stcOrderNo: putAwayOrder.stcOrderNo,
          lockedBy: putAwayOrder.lockedBy
        });
      }
      
      setSelectedOrderId(putAwayOrder.stcOrderNo);
      setCurrentView('putaway');
      
      const duration = Date.now() - startTime;
      logger.info('ModernPutAwayApp', 'Successfully started put-away session', {
        stcOrderNo: putAwayOrder.stcOrderNo,
        duration,
        view: 'putaway'
      });
      
    } catch (err) {
      const duration = Date.now() - startTime;
      const error = err as Error;

      logger.logException('ModernPutAwayApp', error, {
        operation: 'handleSelectPutAway',
        stcOrderNo: putAwayOrder.stcOrderNo,
        duration,
        wasLocked: !!putAwayOrder.lockedBy
      });

      if (err instanceof LockConflictError) {
        alert(`Utleggsordre #${putAwayOrder.stcOrderNo} er allerede under arbeid av ${err.lockedBy}.\n\nVelg en annen utleggsordre.`);
        loadPutAwayOrders(); // Refresh list so the locked item disappears from available
      } else {
        alert(t('warehouse.modules.putaway.errors.lockOrderFailed'));
      }
    }
  };

  const handleNextPutAway = async () => {
    logger.logUserAction('click_next_putaway');

    // Finn høyeste prioritets utplasseringsordre som er tilgjengelig
    // Legacy BizClick Logistics: Active orders have status > 0 AND < 30
    const availableOrders = putAwayOrders.filter(order =>
      order.orderStatus > 0 && order.orderStatus < 30 &&
      (!order.lockedBy || order.lockedBy.trim() === '')
    );

    logger.info('ModernPutAwayApp', 'Searching for next available put-away order', {
      totalOrders: putAwayOrders.length,
      availableCount: availableOrders.length,
      criteria: {
        statusRange: '> 0 AND < 30',
        unlocked: true
      }
    });

    if (availableOrders.length > 0) {
      const highestPriority = availableOrders.reduce((prev, current) =>
        prev.orderPriority > current.orderPriority ? prev : current
      );

      logger.info('ModernPutAwayApp', 'Found highest priority put-away order for next put-away', {
        selectedStcOrderNo: highestPriority.stcOrderNo,
        priority: highestPriority.orderPriority,
        description1: highestPriority.description1,
        candidateCount: availableOrders.length
      });

      await handleSelectPutAway(highestPriority);
    } else {
      logger.warn('ModernPutAwayApp', 'No available put-away orders found for next put-away', {
        totalOrders: putAwayOrders.length,
        lockedCount: putAwayOrders.filter(o => o.lockedBy && o.lockedBy.trim() !== '').length,
        statusFiltered: putAwayOrders.filter(o => o.orderStatus <= 0 || o.orderStatus >= 30).length
      });

      alert(t('warehouse.modules.putaway.messages.noAvailableOrders'));
    }
  };

  const handleBackToList = () => {
    logger.logUserAction('back_to_list', {
      fromStcOrderNo: selectedOrderId,
      previousView: currentView
    });
    
    setCurrentView('list');
    setSelectedOrderId(null);
    loadPutAwayOrders();
    
    logger.info('ModernPutAwayApp', 'Returned to put-away order list view and refreshed data');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12 px-4">
        <div className="w-6 h-6 sm:w-8 sm:h-8 animate-spin border-2 border-green-500 border-t-transparent rounded-full" />
        <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-600">{t('warehouse.modules.putaway.messages.loadingOrders')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <p className="text-red-600 mb-4 text-sm sm:text-base">{error}</p>
        <button
          onClick={() => {
            logger.logUserAction('retry_load_putaway_orders');
            loadPutAwayOrders();
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 min-h-[44px] text-sm sm:text-base"
        >
          {t('common.buttons.tryAgain')}
        </button>
      </div>
    );
  }

  if (currentView === 'putaway' && selectedOrderId) {
    return (
      <ModernPutAwayDetails 
        stcOrderNo={selectedOrderId} 
        onBack={handleBackToList} 
      />
    );
  }

  return (
    <ModernPutAwayList
      putAwayOrders={putAwayOrders}
      onSelectPutAway={handleSelectPutAway}
      onNextPutAway={handleNextPutAway}
      onRefreshOrders={() => {
        logger.logUserAction('refresh_putaway_orders');
        loadPutAwayOrders();
      }}
      onBack={onBack}
    />
  );
};
